import { createAction, handleActions } from 'redux-actions';
import { client as apollo } from 'cccisd-apollo';
import _union from 'lodash/union';
import _omit from 'lodash/omit';

import createdAssessmentPlansQuery from '../assessment/queries/createdAssessmentPlans.graphql';
import studentsPlansListQuery from '../assessment/queries/studentsPlansList.graphql';
import orgLearnersPlansListQuery from '../assessment/queries/orgLearnersPlansList.graphql';
import contactsAndSessionsQuery from '../assessment/queries/contactsAndSessions.graphql';
import defaultProgressQuery from '../assessment/queries/progress.graphql';
import studentNotesQuery from '../assessment/queries/studentNotes.graphql';

const Fortress = window.cccisd.fortress;
const isOrgProvider = Fortress.user.acting.role.handle === 'orgInstructor';
const providerHandle = Fortress.user.acting.role.handle;
const learnerListHandle = isOrgProvider ? 'orgLearnerList' : 'learnerList';

export const initialState = {
    assessmentPlanList: [],
    selectedPlan: {},
    selectedStudent: {},
    studentsPlansList: [],
    sessions: [],
    contacts: [],
    progress: [],
    studentNotesInfo: {},
};

// Actions
const SET_ASSESSMENT_PLAN_LIST = 'app/assessment/SET_ASSESSMENT_PLAN_LIST';
const SET_SELECTED_PLAN = 'app/assessment/SET_SELECTED_PLAN';
const SET_SELECTED_STUDENT = 'app/assessment/SET_SELECTED_STUDENT';
const SET_STUDENTS_PLANS_LIST = 'app/assessment/SET_STUDENTS_PLANS_LIST';
const SET_SESSIONS = 'app/assessment/SET_SESSIONS';
const SET_CONTACTS = 'app/assessment/SET_CONTACTS';
const SET_PROGRESS = 'app/assessment/SET_PROGRESS';
const SET_STUDENT_NOTES_INFO = 'app/assessment/SET_STUDENT_NOTES_INFO';

// Action Creators
export const setAssessmentPlanList = createAction(SET_ASSESSMENT_PLAN_LIST);
export const setSelectedPlan = createAction(SET_SELECTED_PLAN);
export const setSelectedStudent = createAction(SET_SELECTED_STUDENT);
export const setStudentsPlansList = createAction(SET_STUDENTS_PLANS_LIST);
export const setSessions = createAction(SET_SESSIONS);
export const setContacts = createAction(SET_CONTACTS);
export const setProgress = createAction(SET_PROGRESS);
export const setStudentNotesInfo = createAction(SET_STUDENT_NOTES_INFO);

export const loadStudentNotesInfo = ({ pawnId }) => {
    return async (dispatch, getState) => {
        let result = null;
        try {
            result = await apollo.query({
                query: studentNotesQuery,
                fetchPolicy: 'network-only',
                variables: {
                    pawnId,
                },
            });
            dispatch(setStudentNotesInfo(result.data.roles.learner));
        } catch (e) {
            throw e;
        }
    };
};

export const loadStudentsPlansList = ({ forceReload } = {}) => {
    return async (dispatch, getState) => {
        let result = null;
        try {
            result = await apollo.query({
                query: isOrgProvider ? orgLearnersPlansListQuery : studentsPlansListQuery,
                fetchPolicy: 'network-only',
                variables: {
                    instructorPawnId: Fortress.user.acting.id,
                },
            });
            dispatch(setStudentsPlansList(result.data.roles[learnerListHandle]));
        } catch (e) {
            throw e;
        }
    };
};

export const loadAssessmentPlanList = ({ forceReload } = {}) => {
    return async (dispatch, getState) => {
        let result = null;
        try {
            result = await apollo.query({
                query: createdAssessmentPlansQuery,
                variables: {
                    pawnId: Fortress.user.acting.id,
                },
                fetchPolicy: 'network-only',
            });

            let planList = result.data.roles[providerHandle].createdAssignmentPlanList;
            dispatch(setAssessmentPlanList(planList));

            return planList;
        } catch (e) {
            throw e;
        }
    };
};

export const loadContactsAndSessions = ({ assignmentPlanId, pawnIds }) => {
    return async (dispatch, getState) => {
        try {
            const resp = await apollo.query({
                query: contactsAndSessionsQuery,
                variables: {
                    pawnIds,
                    planId: assignmentPlanId,
                    sPlanId: String(assignmentPlanId),
                },
                fetchPolicy: 'network-only',
            });

            const combinedContacts = resp.data.roles[learnerListHandle]
                .map(learner => learner.childRoles.contactList)
                .flat();

            const sessionList = resp.data.flows.assignmentPlan.sessionList;

            dispatch(setContacts(combinedContacts));
            dispatch(
                setSessions(
                    sessionList.map(session => {
                        return { ...session, settings: _omit(session.settings, 'sessionCollection') };
                    })
                )
            );
            return { contacts: combinedContacts, sessions: sessionList };
        } catch (e) {
            throw e;
        }
    };
};

export const loadProgress = ({ sessions, contacts, pawnIds = [], progressQuery }) => {
    return async (dispatch, getState) => {
        let data = [];

        // Collect unique deploymentIds and surveyHandles from every session in current plan
        const combinedSessionsData = {
            deployments: new Set(),
            surveyList: new Set(),
        };

        sessions.forEach(session => {
            if (session.sessionDeploymentInfo) {
                Object.values(session.sessionDeploymentInfo).forEach(s =>
                    combinedSessionsData.deployments.add(s.deploymentId)
                );
            }
            session.settings.options.forEach(surveyHandle => combinedSessionsData.surveyList.add(surveyHandle));
        });

        const metrics = [...combinedSessionsData.surveyList]
            .map(surveyHandle => [surveyHandle + '_start', surveyHandle + '_lastVisited', surveyHandle + '_end'])
            .flat();

        pawnIds = _union(
            pawnIds,
            contacts.map(contact => contact.pawn.pawnId)
        );

        try {
            const resp = await apollo.query({
                // We might need a custom progress query passed in from the app with app devTags
                query: progressQuery ?? defaultProgressQuery,
                variables: {
                    deploymentIds: [...combinedSessionsData.deployments],
                    metrics,
                    surveyHandles: [...combinedSessionsData.surveyList],
                    pawnIds,
                },
                fetchPolicy: 'network-only',
            });

            data = resp.data.flows.assignmentProgressList;
            dispatch(setProgress(data));
        } catch (e) {
            throw e;
        }

        // return data;
    };
};

// reducer
export default handleActions(
    {
        [SET_ASSESSMENT_PLAN_LIST]: (state, action) => {
            return {
                ...state,
                assessmentPlanList: action.payload,
            };
        },
        [SET_SELECTED_PLAN]: (state, action) => {
            return {
                ...state,
                selectedPlan: action.payload,
            };
        },
        [SET_SELECTED_STUDENT]: (state, action) => {
            return {
                ...state,
                selectedStudent: action.payload,
            };
        },
        [SET_STUDENTS_PLANS_LIST]: (state, action) => {
            return {
                ...state,
                studentsPlansList: action.payload,
            };
        },
        [SET_SESSIONS]: (state, action) => {
            return {
                ...state,
                sessions: action.payload,
            };
        },
        [SET_CONTACTS]: (state, action) => {
            return {
                ...state,
                contacts: action.payload,
            };
        },
        [SET_PROGRESS]: (state, action) => {
            return {
                ...state,
                progress: action.payload,
            };
        },
        [SET_STUDENT_NOTES_INFO]: (state, action) => {
            return {
                ...state,
                studentNotesInfo: action.payload,
            };
        },
    },
    initialState
);
